import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { ConfirmDialog } from '../../features/appointments/action-dialogs';
import { EligibilityRulesScreen } from '../../features/eligibility-rules/eligibility-rules-screen';
import { ELIGIBILITY_PERMISSIONS } from '../../features/eligibility-rules/types';
import { PricingTabs } from '../../lib/pricing-tabs';
import { RequirePermission } from '../../lib/session/guards';

export const Route = createFileRoute('/pricing/eligibility')({
  component: EligibilityRulesPage,
});

/**
 * `/pricing/eligibility` — the scheduling rules builder (M15, ADR-032).
 *
 * `pricing.read` opens it and runs the simulator; publishing and restoring need
 * `pricing.manage`, which the screen reads from the session to decide whether the controls
 * exist — and which the server checks again whatever the buttons look like (rules §17.2,
 * §22.2). The design adds no new permission keys.
 *
 * The route owns the one thing that needs the router: the unsaved-changes guard. The draft
 * lives only in memory, so navigating away with unpublished edits asks first, and closing
 * the tab gets the browser's own prompt.
 */
function EligibilityRulesPage() {
  const [dirty, setDirty] = useState(false);
  const onDirtyChange = useCallback((next: boolean) => {
    setDirty(next);
  }, []);

  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  return (
    <RequirePermission keys={[ELIGIBILITY_PERMISSIONS.read]}>
      <EligibilityRulesScreen tabs={<PricingTabs />} onDirtyChange={onDirtyChange} />
      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onClose={() => {
          blocker.reset?.();
        }}
        destructive={true}
        title="Leave without publishing?"
        description="The scheduling rules have changes that are not published. There is no draft saved on the server."
        confirmLabel="Leave and lose changes"
        onConfirm={() => {
          blocker.proceed?.();
        }}
      >
        <p className="text-base text-neutral-600">Stay on the page to publish them first.</p>
      </ConfirmDialog>
    </RequirePermission>
  );
}
